import { inject, Injectable } from '@angular/core';
import { CoreListEnvelop } from '@mkp/shared/models';
import { BaseHttpResource } from '@mkp/shared/data-access';
import { nullIfEmpty } from '@mkp/shared/util-api';
import { createRandomString, getAuthDomain } from '@mkp/shared/util-auth';
import { CLIENT_ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment/client';
import { decodePunycodeEmail } from '@mkp/shared/util-format';
import {
  CreatePasswordlessUserPayload,
  CreateUserPayload,
  PasswordlessUserCreateDto,
  UserCreateDto,
} from '@mkp/user/models';
import { map, Observable } from 'rxjs';
import { ResetPasswordBody, ResetPasswordDto } from './reset-password.dto';
import { UserDto } from './user.dto';

@Injectable({ providedIn: 'root' })
export class UserResource extends BaseHttpResource<UserDto, CoreListEnvelop<UserDto>> {
  private readonly auth = inject(CLIENT_ENVIRONMENT_PROVIDER).auth;

  constructor() {
    super('user');
  }

  resetPassword(email: string): Observable<ResetPasswordDto> {
    const url = `https://${getAuthDomain(this.auth)}/dbconnections/change_password`;
    const body: ResetPasswordBody = {
      email,
      client_id: this.auth.clientId,
      connection: 'Username-Password-Authentication',
    };

    return this.http.post<ResetPasswordDto>(url, body);
  }

  getByAccountId(accountId: string): Observable<UserDto[]> {
    return this.getWithQuery({
      filter: `accountMemberships.account.id==${accountId}`,
      limit: 50,
    }).pipe(map((response) => response._embedded.results.map(decodeUserDtoEmail)));
  }

  createUser(createUserPayload: CreateUserPayload) {
    const userDto: UserCreateDto = {
      email: createUserPayload.email.toLowerCase(),
      password: createRandomString(20),
      title: createUserPayload.title,
      firstName: createUserPayload.firstName,
      lastName: createUserPayload.lastName,
      profilePictureMediaApiId: nullIfEmpty(createUserPayload.profilePictureMediaApiId),
      phoneNumber: nullIfEmpty(createUserPayload.phoneNumber),
      gtcAcceptedAt: new Date().toISOString(),
      externalSource: null,
      externalId: null,
      settings: {
        displayType: createUserPayload.displayType,
        language: createUserPayload.language,
        marketingConsentAcceptedAt: getMarketingConsentAcceptedAt(
          createUserPayload.marketingConsent
        ),
        publicationAiUsageConsent: createUserPayload.publicationAiUsageConsent,
      },
      suppressWelcomeEmail: null,
    };

    return this.http.post<UserDto>(this.url, userDto);
  }

  createPasswordlessUser({
    email,
    displayType,
    language,
  }: CreatePasswordlessUserPayload): Observable<UserDto> {
    const passwordlessUserDto: PasswordlessUserCreateDto = {
      email: email.toLowerCase(),
      gtcAcceptedAt: new Date().toISOString(),
      settings: {
        displayType,
        language,
      },
    };
    return this.http.post<UserDto>(this.url + '/passwordless', passwordlessUserDto);
  }
}

const getMarketingConsentAcceptedAt = (marketingConsent: boolean): string | null =>
  marketingConsent ? new Date().toISOString() : null;

/**
 * Decodes punycode email in UserDto for display
 * This ensures internationalized domain names are human-readable
 */
const decodeUserDtoEmail = (user: UserDto): UserDto => ({
  ...user,
  email: decodePunycodeEmail(user.email),
});
